import http from "../../http";
import { Order } from "../../../../domain/entities/shop/order/order";
import { Receipt } from "../../../../domain/entities/shop/order/receipt";
import { Filter } from "../../../../domain/entities/shop/filter/filter";
import { Product } from "../../../../domain/entities/shop/product/product";
import { ShopRepository } from "./shop_interface";
import { ListResponse } from "../../../../domain/entities/shop/product/listResponse";
import { wrapErrorHandler } from "../../utils/wrap_error_handler";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";

/** Function responsible for formatting the selected filters.
 * This function is called when the request is made, passing the formatted parameters in the GET of the URL. */
function encodeFilterToURL(filter: Filter): string {
  /** Concatenates the array of category ids with the string '&' and strips all spaces. */
  const categories = filter.categories
    .map(a => `category=${a.id}`)
    .join("&")
    .trim();

  /** Concatenates the array of shops ids with the string '&' and strips all spaces. */
  const shops = filter.shops
    .map(a => `shop=${a.id}`)
    .join("&")
    .trim();

  /** Concatenates the array of prices ids with the string '&' and strips all spaces. */
  const prices = filter.prices
    .map(e => `interval=(${e.min},${e.max})`)
    .join("&")
    .trim();

  const search = `search=${filter.searchQuery}`;

  /** Variable that receives all formatted parameters.
   * Ex.: category=1&category=3&shop=8&interval=(10,20) */
  let params = "";

  /** Checks if there are categories inside the array.
   * If it exists, it is checked if there are already parameters in the URl.
   * If so, the string '&' is added.
   * If it doesn't mean it is the first parameter of the URL then the string '?' is added. */
  if (filter.categories.length) {
    if (params.length) {
      params += `&${categories}`;
    } else {
      params += `?${categories}`;
    }
  }

  if (filter.shops.length) {
    if (params.length) {
      params += `&${shops}`;
    } else {
      params += `?${shops}`;
    }
  }

  if (filter.prices.length) {
    if (params.length) {
      params += `&${prices}`;
    } else {
      params += `?${prices}`;
    }
  }

  if (filter.searchQuery.length) {
    if (params.length) {
      params += `&${search}`;
    } else {
      params += `?${search}`;
    }
  }

  return params;
}

class _Shop implements ShopRepository {
  async listFilters(filter: Filter): Promise<Filter> {
    return wrapErrorHandler<Filter>(async () => {
      const response = await http.get(
        `/api/decola/list/filters${encodeFilterToURL(filter)}`
      );
      return response.data;
    });
  }

  async filterProducts(
    filter: Filter,
    page: number
  ): Promise<ListResponse<Product>> {
    return wrapErrorHandler<ListResponse<Product>>(async () => {
      const response = await http.get(
        `/api/decola/list/products${encodeFilterToURL(filter)}`,
        {
          params: {
            limit: 20,
            page: page
          }
        }
      );
      return response.data;
    });
  }

  async products(): Promise<ListResponse<Product>> {
    return wrapErrorHandler<ListResponse<Product>>(async () => {
      const response = await http.get(`/api/decola/list/products`);
      return response.data;
    });
  }

  async addToFavorite(id: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      await http.post(`/api/decola/save/user/favorite/product/id/${id}`);
    });
  }

  async listFavoriteProducts(
    page?: number,
    limit?: number
  ): Promise<ListResponse<Product>> {
    return wrapErrorHandler<ListResponse<Product>>(async () => {
      const params: URLSearchParams = new URLSearchParams();

      if (page && limit) {
        params.append("page", String(page));
        params.append("limit", String(limit));
      }

      const response = await http.get(
        `/api/decola/list/user/favorite/product`,
        {
          params
        }
      );

      return response.data;
    });
  }

  async removeProductFromFavorites(id: number): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      await http.delete(`/api/decola/delete/user/favorite/product/id/${id}`);
    });
  }

  async getProduct(id: number): Promise<Product> {
    return wrapErrorHandler<Product>(async () => {
      const response = await http.get(`/api/decola/get/product/id/${id}`);
      return response.data;
    });
  }

  async order(order: Order): Promise<Receipt> {
    return wrapErrorHandler<Receipt>(async () => {
      const response = await http.post(`/api/decola/order`, order);
      return response.data;
    });
  }

  async getReceipt(orderID: number): Promise<Receipt> {
    return wrapErrorHandler<Receipt>(async () => {
      const response = await http.get(`/api/decola/order/${orderID}`);
      return response.data;
    });
  }

  async orders(): Promise<Receipt[]> {
    return wrapErrorHandler<Receipt[]>(async () => {
      const response = await http.get(`/api/decola/orders`);
      return response.data;
    });
  }

  async createReceipt(orderID: number): Promise<Receipt> {
    return wrapErrorHandler<Receipt>(async () => {
      const response = await http.post(`/api/decola/receipt/${orderID}`);
      return response.data;
    });
  }

  getProductsFromBag(): Product[] {
    try {
      let products: Product[] = [];
      const stringProducts = localStorage.getItem("bagProducts");
      if (stringProducts) {
        const localStorageProducts = JSON.parse(stringProducts);
        products = [...localStorageProducts];
      }
      return products;
    } catch (e) {
      console.error(e);
      throw new RepositoryException(
        "Ocorreu um erro inesperado.",
        "UNEXPECTED_ERROR"
      );
    }
  }

  setProductToBag(product: Product): void {
    try {
      const products = this.getProductsFromBag();
      products.push(product);
      localStorage.setItem("bagProducts", JSON.stringify(products));
    } catch (e) {
      console.error(e);
      throw new RepositoryException(
        "Ocorreu um erro inesperado.",
        "UNEXPECTED_ERROR"
      );
    }
  }

  setBagProducts(products: Product[]): void {
    try {
      localStorage.setItem("bagProducts", JSON.stringify(products));
    } catch (e) {
      console.error(e);
      throw new RepositoryException(
        "Ocorreu um erro inesperado.",
        "UNEXPECTED_ERROR"
      );
    }
  }

  removeBagProduct(product: Product): void {
    try {
      const productsBag = this.getProductsFromBag();
      const products = productsBag.filter(item => product.id !== item.id);
      this.setBagProducts(products);
    } catch (e) {
      console.error(e);
      throw new RepositoryException(
        "Ocorreu um erro inesperado.",
        "UNEXPECTED_ERROR"
      );
    }
  }

  removeAllBagProducts(): void {
    try {
      const productsBag = [];
      this.setBagProducts(productsBag);
    } catch (e) {
      console.error(e);
      throw new RepositoryException(
        "Ocorreu um erro inesperado.",
        "UNEXPECTED_ERROR"
      );
    }
  }
}

export const shop_repository: ShopRepository = new _Shop();
